import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Calendar, Clock, Share2, Heart, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { blogs } from '../data/blogs';
import SEO from '@/components/SEO';

// Add Buffer polyfill for browser environment
if (typeof Buffer === 'undefined') {
  global.Buffer = require('buffer').Buffer;
}

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState<any>(null);
  const [content, setContent] = useState<string>('');
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
  const [isLiked, setIsLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [views, setViews] = useState(0);

  useEffect(() => {
    const foundBlog = blogs.find(b => b.link.split('/').pop() === slug);
    if (foundBlog) {
      setBlog(foundBlog);
      setLikes(Math.floor(Math.random() * 500) + 100);
      setViews(Math.floor(Math.random() * 5000) + 1000);
      
      // Find related posts (excluding current)
      const related = blogs.filter(b => b.link.split('/').pop() !== slug).slice(0, 3);
      setRelatedPosts(related);

      // Load markdown content
      const loadContent = async () => {
        try {
          const contentModule = await import(`../content/blogs/${slug}.md?raw`);
          setContent(contentModule.default);
        } catch (error) {
          console.error('Error loading blog content:', error);
          setContent(`# ${foundBlog.title}\n\n${foundBlog.summary}\n\nContent is being loaded...`);
        }
      };
      
      loadContent();
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blog?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikes(prev => isLiked ? prev - 1 : prev + 1);
  };

  if (!blog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center p-8"
        >
          <h1 className="text-3xl font-bold mb-4 text-gray-800">Article Not Found</h1>
          <p className="text-gray-600 mb-8">Sorry, the article you're looking for doesn't exist or has been moved.</p>
          <Link to="/blog">
            <Button className="bg-red-500 hover:bg-red-600 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const blogPostStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blog.title,
    "description": blog.summary,
    "url": `https://dailzy.com${blog.link}`,
    "datePublished": blog.date,
    "dateModified": blog.date,
    "author": {
      "@type": "Organization",
      "name": "Dailzy Team",
      "url": "https://dailzy.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Dailzy",
      "logo": {
        "@type": "ImageObject", 
        "url": "https://dailzy.com/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://dailzy.com${blog.link}`
    },
    "image": {
      "@type": "ImageObject",
      "url": "https://dailzy.com/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png"
    },
    "articleSection": "Quick Commerce",
    "keywords": ["quick commerce", "delivery", "local business", "hyperlocal"],
    "wordCount": content.length,
    "interactionStatistic": [
      {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/LikeAction",
        "userInteractionCount": likes
      },
      {
        "@type": "InteractionCounter", 
        "interactionType": "https://schema.org/ViewAction",
        "userInteractionCount": views
      }
    ],
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://dailzy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2, 
          "name": "Blog",
          "item": "https://dailzy.com/blog"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": blog.title,
          "item": `https://dailzy.com${blog.link}`
        }
      ]
    }
  };

  const articleTags = slug?.includes('quick') ? ['quick commerce', 'delivery', 'hyperlocal'] : 
                     slug?.includes('vendor') ? ['vendors', 'local business', 'partnerships'] :
                     ['shopping', 'tips', 'lifestyle'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50">
      <SEO
        title={`${blog.title} | Dailzy Blog`}
        description={blog.summary}
        keywords={`${blog.title.toLowerCase()}, quick commerce blog, ${articleTags.join(', ')}, dailzy insights`}
        url={`https://dailzy.com${blog.link}`}
        type="article"
        article={{
          publishedTime: blog.date,
          modifiedTime: blog.date,
          author: "Dailzy Team",
          section: "Quick Commerce",
          tags: articleTags
        }}
        structuredData={blogPostStructuredData}
      />
      {/* Hero Section */}
      <section className="relative py-8 overflow-hidden">        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            {/* Back Button */}
            <Link 
              to="/blog" 
              className="inline-flex items-center text-red-500 hover:text-red-600 mb-6 font-semibold group transition-all"
            >
              <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </Link>

            {/* Article Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-600">
              <div className="flex items-center">
                <Calendar size={14} className="mr-2 text-red-500" />
                {new Date(blog.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div className="flex items-center">
                <Clock size={14} className="mr-2 text-red-500" />
                5 min read
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">
              {blog.title}
            </h1>

            {/* Summary */}
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              {blog.summary}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-4">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={blog.image} 
                alt={blog.title} 
                className="w-full h-48 md:h-64 lg:h-80 object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Main Content */}
            <motion.article 
              className="w-full"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm border border-gray-100">
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mb-8 pb-6 border-b border-gray-100">
                  <Button 
                    onClick={handleShare}
                    size="sm"
                    variant="outline" 
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Share2 size={14} className="mr-2" />
                    Share
                  </Button>
                  <Button 
                    size="sm"
                    variant="outline" 
                    className="border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    <Heart size={14} className="mr-2" />
                    Save
                  </Button>
                </div>

                <div className="prose prose-lg prose-red max-w-none">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => (
                        <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-6 mt-8 first:mt-0">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4 mt-8">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 mt-6">
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                          {children}
                        </p>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal list-inside text-gray-600 mb-6 space-y-2">
                          {children}
                        </ol>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-red-500 pl-6 my-8 bg-red-50 py-4 rounded-r-lg">
                          <div className="text-gray-700 italic text-lg">
                            {children}
                          </div>
                        </blockquote>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-bold text-gray-800">
                          {children}
                        </strong>
                      ),
                      em: ({ children }) => (
                        <em className="italic text-gray-700">
                          {children}
                        </em>
                      ),
                    }}
                  >
                    {content}
                  </ReactMarkdown>
                </div>

                {/* Article Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Heart size={14} className="mr-2" />
                      Like
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-gray-200 text-gray-600 hover:bg-gray-50"
                    >
                      <MessageCircle size={14} className="mr-2" />
                      Comment
                    </Button>
                  </div>
                </div>
              </div>
            </motion.article>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      {/* <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-3">Stay Updated with Dailzy</h3>
              <p className="text-red-100 mb-6">
                Get the latest articles and updates about quick commerce delivered to your inbox
              </p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-lg text-gray-800 border-0 focus:ring-2 focus:ring-white focus:outline-none"
                />
                <Button className="bg-white text-red-500 hover:bg-gray-100 px-6">
                  Subscribe
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default BlogDetail; 